/**
 * Командный пульт «Журнал сроков обучения» (`/team/training`, canon service-training — первичный
 * контур по всем клиентам). Обязательный фильтр по клиенту: команда сначала выбирает клиента, затем
 * грузит журнал (teamApi.training.journal). Общий компонент TrainingJournal с canRemind=true —
 * ручной триггер «Напомнить» (Блок 7 отдаёт 202). Ссылка на каталог программ — всей команде
 * (training-packages-approval), на справочник должностей — админу (p2-position-dictionary,
 * tasks.md 4.2 — вход рядом с каталогом программ).
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GraduationCap } from 'lucide-react'
import { teamApi, type ClientSummary, type JournalResponse, type TrainingDirection } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { useAuth } from '@/app/auth-context'
import { ClientSelect } from '@/company/client-select'
import { TrainingJournal } from '@/training/training-journal'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'

export function TeamTrainingPage() {
  const navigate = useNavigate()
  const { context } = useAuth()
  const isAdmin = context?.user.role === 'admin'

  // Выбранный клиент целиком: имя нужно и для выгрузки журнала, и как метка поля —
  // список вариантов живёт внутри автокомплита и здесь не хранится (centrika-docs!45).
  const [client, setClient] = useState<ClientSummary | null>(null)
  const clientId = client?.id ?? ''
  const [data, setData] = useState<JournalResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) {
      setData(null)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    teamApi.training
      .journal({ client: clientId }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setData(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить журнал'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [clientId])

  const clientName = client?.name

  async function remind(personId: string, direction?: TrainingDirection) {
    await teamApi.training.remind(clientId, { personId, direction })
  }

  return (
    <div>
      <PageHead
        title="Журнал сроков обучения"
        subtitle="Контроль сроков обучения по клиентам — выберите клиента"
        actions={
          <>
            {/* Каталог — всей команде: добавить программу или пакет может любой сотрудник
                (training-packages-approval); справочник должностей — только админу. */}
            <Button variant="secondary" onClick={() => navigate('/training/programs')}>
              Каталог программ
            </Button>
            {isAdmin ? (
              <Button variant="secondary" onClick={() => navigate('/positions')}>
                Справочник должностей
              </Button>
            ) : null}
          </>
        }
      />

      <Toolbar>
        <div className="max-w-narrow flex-1">
          <ClientSelect
            value={clientId}
            valueLabel={clientName}
            onSelect={setClient}
            aria-label="Клиент"
          />
        </div>
      </Toolbar>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : !clientId ? (
        <Panel>
          <EmptyState
            icon={GraduationCap}
            title="Выберите клиента"
            description="Журнал сроков обучения показывается по выбранному клиенту"
          />
        </Panel>
      ) : loading || !data ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <TrainingJournal
          data={data}
          canRemind
          onRemind={remind}
          exportSuffix={clientName ?? clientId}
        />
      )}
    </div>
  )
}
